/**
 * Minimum cart quantity validation
 * Ensures checkout is disabled when cart has fewer than 6 items
 */

class CartMinimumQuantity {
  constructor() {
    this.minQuantity = 6;
    this.init();
  }

  init() {
    // Listen for cart update events
    this.setupEventListeners();
    
    // Initial check
    this.updateCheckoutButton();
  }

  setupEventListeners() {
    // Listen for custom cart update events
    document.addEventListener('cart:updated', () => {
      this.updateCheckoutButton();
    });

    document.addEventListener('cart:refresh', () => {
      this.updateCheckoutButton();
    });

    // Listen for quantity changes
    document.addEventListener('quantity-selector:update', () => {
      // Small delay to let the cart update
      setTimeout(() => this.updateCheckoutButton(), 100);
    });

    // Listen for variant picker updates (might affect cart)
    document.addEventListener('variant:update', () => {
      setTimeout(() => this.updateCheckoutButton(), 100);
    });

    // Monitor DOM changes for cart updates
    const observer = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      
      mutations.forEach((mutation) => {
        const target = mutation.target;
        
        // Check if cart items changed
        if (target instanceof Element && 
            (target.closest('.cart-items') || 
             target.closest('.cart__summary-totals'))) {
          shouldUpdate = true;
        }
        
        // Check if quantity inputs changed
        if (mutation.type === 'attributes' && 
            mutation.attributeName === 'value' &&
            target instanceof Element &&
            target.matches('input[name*="updates"]')) {
          shouldUpdate = true;
        }
      });

      if (shouldUpdate) {
        setTimeout(() => this.updateCheckoutButton(), 100);
      }
    });

    // Observe cart areas
    const cartAreas = document.querySelectorAll('.cart-items, .cart__summary-totals, .cart-drawer__summary');
    cartAreas.forEach(area => {
      observer.observe(area, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['value']
      });
    });
  }

  async updateCheckoutButton() {
    try {
      // Fetch current cart data
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Calculate total quantity
      const totalQty = cart.items.reduce((/** @type {number} */ sum, /** @type {{quantity: number | string}} */ item) => {
        // Ensure we're working with numbers
        const itemQty = parseInt(String(item.quantity)) || 0;
        return sum + itemQty;
      }, 0);
      
      // Find all checkout buttons, messages, and accelerated checkout buttons
      const checkoutButtons = document.querySelectorAll('.cart__checkout-button');
      const messages = document.querySelectorAll('.cart-min-qty-message');
      const acceleratedButtons = document.querySelectorAll('.additional-checkout-buttons');
      
      // Update buttons
      checkoutButtons.forEach(button => {
        if (!button) return;
        
        if (totalQty < this.minQuantity) {
          button.setAttribute('disabled', 'disabled');
          button.setAttribute('aria-disabled', 'true');
        } else {
          button.removeAttribute('disabled');
          button.removeAttribute('aria-disabled');
        }
      });
      
      // Update accelerated checkout buttons (PayPal, etc.)
      acceleratedButtons.forEach(container => {
        if (!container || !(container instanceof HTMLElement)) return;

        if (totalQty < this.minQuantity) {
          container.style.display = 'none';
        } else {
          container.style.display = '';
        }
      });

      // Update messages
      messages.forEach(message => {
        if (!message) return;
        
        if (totalQty < this.minQuantity) {
          message.textContent = `You must have at least ${this.minQuantity} items in your cart to check out. You currently have ${totalQty}.`;
          if (message instanceof HTMLElement) {
            message.style.display = 'block';
          }
        } else {
          if (message instanceof HTMLElement) {
            message.style.display = 'none';
          }
        }
      });
      
      console.log(`Cart quantity updated: ${totalQty} items (min: ${this.minQuantity})`);
    } catch (error) {
      console.error('Error updating cart minimum quantity:', error);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new CartMinimumQuantity());
} else {
  new CartMinimumQuantity();
}
